import type { ToolCallMessagePart } from '@assistant-ui/react';
import { z } from 'zod';

import { ERROR_RECOVERY_STRATEGIES } from './errors/base.ts';

/**
 * Successful tool execution result.
 *
 * Carries no human-readable prose — message text is derived on-demand
 * from the tool's `message.result` formatter at render/transport time
 * (passing `(args, dataset?)` for producer tools, `(args, output)` for
 * non-producer tools). Tool-specific output payload (e.g.
 * ask-user-question's `messages` field) is allowed via the
 * discriminated extend.
 */
export const ToolOutputSuccessSchema = z.object({
  success: z.literal(true),
});
/** @public */
export type ToolOutputSuccess = z.infer<typeof ToolOutputSuccessSchema>;

/**
 * Producer-tool success — semantically identical to
 * {@link ToolOutputSuccessSchema}. The `outputDataset` is already in
 * `args` (and in the canonical TanStack query key), so the envelope
 * itself carries no payload.
 */
export const DatasetProducerSuccessSchema = ToolOutputSuccessSchema;
/** @public */
export type DatasetProducerSuccess = z.infer<typeof DatasetProducerSuccessSchema>;

/**
 * Failed tool execution result.
 *
 * Mirrors {@link ToolErrorResponse} (the structured wire shape produced by
 * `ToolExecutionError.toStructuredResponse()`). The full structured
 * payload — errorType, error message, suggestions, details, recovery —
 * is carried across the wire so consumers (`formatErrorText`, the
 * assistant-ui error part, the "Show details" panel) can render it
 * consistently without round-tripping through pre-rendered prose.
 */
export const ToolOutputErrorSchema = z.object({
  success: z.literal(false),
  /** Class name of the thrown error — stable, machine-readable discriminator. */
  errorType: z.string(),
  /** Human-readable error message. */
  error: z.string(),
  /** Actionable recovery hints for the LLM. */
  suggestions: z.array(z.string()).readonly().optional(),
  /** Structured fields the UI / LLM can branch on without parsing the message. */
  details: z.record(z.string(), z.unknown()).optional(),
  /** Recovery strategy hint — what should the LLM do next. */
  recovery: z.enum(ERROR_RECOVERY_STRATEGIES).optional(),
});
/** @public */
export type ToolOutputError = z.infer<typeof ToolOutputErrorSchema>;

/**
 * Shared discriminated union for dataset-producing tools.
 */
export const DatasetProducerResultSchema = z.discriminatedUnion('success', [
  DatasetProducerSuccessSchema,
  ToolOutputErrorSchema,
]);
/** @public */
export type DatasetProducerResult = z.infer<typeof DatasetProducerResultSchema>;

/**
 * Tool Output Contract — discriminated union on `success` field.
 *
 * All tools should return this envelope. The discriminated union enables
 * type-safe handling of success/error cases at the call boundary.
 *
 * @example
 * ```ts
 * // Success — producer or non-producer (no prose in the envelope)
 * return { success: true };
 *
 * // Non-producer success that carries its own payload (e.g. ask-user-question)
 * return { success: true, messages: [...] };
 *
 * // Error case (already-rendered prose, no structured fields)
 * return { success: false, errorMessage: 'Dataset "wells_2024" not found...' };
 * ```
 */
export const ToolOutputContractSchema = z.discriminatedUnion('success', [
  ToolOutputSuccessSchema,
  ToolOutputErrorSchema,
]);
/** @public */
export type ToolOutputContract = z.infer<typeof ToolOutputContractSchema>;

/**
 * Tool call part extracted from assistant-ui messages.
 *
 * Structural alias of assistant-ui's {@link ToolCallMessagePart} with two
 * relaxations preserved from the historical local definition:
 *
 * - `args` is widened to `Record<string, unknown>` (canonical default is
 *   `ReadonlyJSONObject`). All accesses in this codebase are reads, and
 *   tests construct partial fixtures, so the wider type is harmless.
 * - `argsText` is optional. The canonical type makes it required, but
 *   internal/test constructions don't always materialize it.
 */
export type ToolCallPart = Omit<ToolCallMessagePart<Record<string, unknown>, unknown>, 'argsText'> & {
  argsText?: string;
};

/**
 * Runtime shape of an assistant-ui message: a `role` plus a `content` array
 * of parts. Used to filter raw `unknown[]` message streams before extracting
 * tool calls.
 */
const MessageSchema = z.object({
  role: z.string(),
  content: z.array(z.unknown()),
});

/**
 * Runtime shape of a tool-call message part. Lenient by design — we don't
 * validate `result`, `argsText`, or status fields, which vary across tools
 * and across the streaming lifecycle.
 */
const ToolCallPartSchema = z.object({
  type: z.literal('tool-call'),
  toolCallId: z.string(),
  toolName: z.string(),
  args: z.record(z.string(), z.unknown()),
});

/**
 * Type guard for message objects with role and content array.
 * Used to filter assistant-ui messages before extracting tool calls.
 */
export function isMessage(value: unknown): value is { role: string; content: unknown[] } {
  return MessageSchema.safeParse(value).success;
}

/**
 * Type guard for tool call parts.
 *
 * Lenient on purpose — `result`, `argsText`, and status fields vary by tool
 * and lifecycle stage, so we only validate the keys every tool call carries.
 */
export function isToolCallPart(value: unknown): value is ToolCallPart {
  return ToolCallPartSchema.safeParse(value).success;
}

/**
 * Predicate: does a tool-execution output value look like a failure?
 *
 * Recognises the canonical failure envelopes emitted by the `<Tool>`
 * execute boundary:
 *   - {@link ToolOutputErrorSchema}: `{ success: false, errorMessage: string }`
 *   - SDK-level parse failures: `{ error: string }` without a `success` field
 *
 * Used by the chat runtime to flip a tool-call part into the `output-error`
 * state without relying on substring matching against the rendered string —
 * tools whose `message` happens to mention the word "error" (e.g. "0 errors
 * found") were previously misreported as failures.
 */
export function isToolErrorOutput(output: unknown): boolean {
  if (typeof output !== 'object' || output === null) {
    return false;
  }
  const obj = output as Record<string, unknown>;
  if (obj.success === false) {
    return true;
  }
  if ('error' in obj && obj.error != null) {
    return true;
  }
  return false;
}
